import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "onChange", "onOpen", "onClose", "labelKey", "label", "groupBy", "options", "blankOption", "value", "disabled", "loading", "multiple", "isOptionEqualToValue", "isShowingSubmenu", "optionsScrollProps", "theme", "onSelectSameOption"];
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { EmbersInnerThemeProvider } from '../../rtlProvider';
import { EmbersMenuItem } from '../../menuItem/menuItem';
import { MiniSelectLaunchButton, miniSelectTheme } from './miniSelectStyles';
import { EmbersLoading } from '../../loading/loading';
import { EmbersMenu } from '../../menu/menu';
import { SelectGroupItem } from '../sharedSelectStyles';
import { onKeyPress } from '../../utils/wcagUtils';
import { getIconNumberClassName } from '../../utils/iconUtils';
import isEqual from 'lodash.isequal';
export function EmbersMiniSelect(props) {
  var className = props.className,
    onChange = props.onChange,
    onOpen = props.onOpen,
    onClose = props.onClose,
    labelKey = props.labelKey,
    label = props.label,
    groupBy = props.groupBy,
    _options = props.options,
    blankOption = props.blankOption,
    value = props.value,
    disabled = props.disabled,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$multiple = props.multiple,
    multiple = _props$multiple === void 0 ? false : _props$multiple,
    isOptionEqualToValue = props.isOptionEqualToValue,
    _props$isShowingSubme = props.isShowingSubmenu,
    isShowingSubmenu = _props$isShowingSubme === void 0 ? false : _props$isShowingSubme,
    optionsScrollProps = props.optionsScrollProps,
    _props$theme = props.theme,
    theme = _props$theme === void 0 ? 'light' : _props$theme,
    onSelectSameOption = props.onSelectSameOption,
    otherProps = _objectWithoutProperties(props, _excluded);
  var launchButtonElemRef = useRef(null);
  var valueOnOpen = useRef(undefined);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    options = _useState4[0],
    setOptions = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasFocus = _useState6[0],
    setHasFocus = _useState6[1];
  var _useState7 = useState(new Array()),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedOptionIndexes = _useState8[0],
    setSelectedOptionIndexes = _useState8[1];
  useEffect(function () {
    setOpen(isShowingSubmenu);
  }, [isShowingSubmenu]);
  useEffect(function () {
    if (open) {
      valueOnOpen.current = value;
    }
  }, [open]);
  useEffect(function () {
    if (!multiple && blankOption) {
      setOptions([blankOption].concat(_options));
    } else {
      setOptions(_options);
    }
  }, [multiple, _options, blankOption]);
  useEffect(function () {
    var selectedIndexes = new Array();
    if (value) {
      var selectedValues = Array.isArray(value) ? value : [value];
      selectedValues.forEach(function (selectedValue) {
        var selectedIndex = options.findIndex(function (option) {
          if (isOptionEqualToValue) {
            return isOptionEqualToValue(option, selectedValue);
          } else {
            return isEqual(option, selectedValue);
          }
        });
        if (selectedIndex !== -1) {
          selectedIndexes.push(selectedIndex);
        }
      });
    }
    setSelectedOptionIndexes(selectedIndexes);
  }, [value, options]);
  var handleMenuItemClick = function handleMenuItemClick(evt, itemIndex) {
    var changedOption = options[itemIndex];
    var reason = 'selectOption';
    if (multiple) {
      var newSelectedIndexes;
      if (selectedOptionIndexes.includes(itemIndex)) {
        reason = 'removeOption';
        newSelectedIndexes = selectedOptionIndexes.filter(function (v) {
          return v !== itemIndex;
        });
      } else {
        newSelectedIndexes = selectedOptionIndexes.slice();
        newSelectedIndexes.push(itemIndex);
      }
      setSelectedOptionIndexes(newSelectedIndexes);
      if (onChange) {
        var selectedOptions = newSelectedIndexes.map(function (index) {
          return options[index];
        });
        onChange(selectedOptions, reason, changedOption, evt);
      }
    } else {
      // single-select
      var selectionOpenedSubmenu = false;
      if (selectedOptionIndexes.length === 0 || selectedOptionIndexes[0] !== itemIndex) {
        var selectedOptionIndex = itemIndex;
        if (blankOption) {
          var isBlankOption = isOptionEqualToValue ? isOptionEqualToValue(changedOption, blankOption) : changedOption === blankOption;
          if (isBlankOption) {
            selectedOptionIndex = -1;
            reason = 'removeOption';
          }
        }
        if (selectedOptionIndex > -1) {
          setSelectedOptionIndexes([selectedOptionIndex]);
        } else {
          setSelectedOptionIndexes([]);
        }
        if (onChange) {
          selectionOpenedSubmenu = onChange(options[selectedOptionIndex], reason, changedOption, evt);
        }
      } else if (selectedOptionIndexes[0] === itemIndex) {
        onSelectSameOption === null || onSelectSameOption === void 0 || onSelectSameOption(evt, itemIndex);
      }
      if (!selectionOpenedSubmenu) {
        handleClose(evt);
      }
    }
  };
  var renderMenuItem = function renderMenuItem(option, index) {
    var _otherProps$getOption;
    var optionDisabled = (_otherProps$getOption = otherProps.getOptionDisabled) === null || _otherProps$getOption === void 0 ? void 0 : _otherProps$getOption.call(otherProps, option);
    var key = labelKey ? "".concat(option[labelKey], "-").concat(index) : "".concat(option, "-").concat(index);
    return /*#__PURE__*/React.createElement(EmbersMenuItem, {
      key: key,
      checkable: multiple,
      onCloseRequest: handleClose,
      selected: selectedOptionIndexes.includes(index),
      disabled: optionDisabled,
      className: "EmbersMiniSelect-option",
      onClick: function onClick(e) {
        return handleMenuItemClick(e, index);
      },
      suffixIcon: option.icon && /*#__PURE__*/React.createElement("i", {
        className: option.icon
      })
    }, labelKey ? option[labelKey] : option);
  };
  var renderMenuItems = function renderMenuItems() {
    var menuItems = new Array();
    if (groupBy) {
      var groupedOptions = new Map();
      options.forEach(function (option) {
        var _groupedOptions$get;
        if (!groupedOptions.has(groupBy(option))) {
          groupedOptions.set(groupBy(option), []);
        }
        (_groupedOptions$get = groupedOptions.get(groupBy(option))) === null || _groupedOptions$get === void 0 || _groupedOptions$get.push(option);
      });
      var optionIndex = 0;
      groupedOptions.forEach(function (optionGroup, grpIndex) {
        menuItems.push( /*#__PURE__*/React.createElement(SelectGroupItem, {
          disabled: true,
          key: 'group' + grpIndex,
          className: "SelectGroupItem-root"
        }, groupBy(optionGroup[0])));
        optionGroup.forEach(function (option) {
          menuItems.push(renderMenuItem(option, optionIndex));
          optionIndex++;
        });
      });
    } else {
      options.forEach(function (option, index) {
        menuItems.push(renderMenuItem(option, index));
      });
    }
    return menuItems;
  };
  var handleOpenMenuRequest = function handleOpenMenuRequest(buttonElem) {
    launchButtonElemRef.current = buttonElem;
    setOpen(true);
    onOpen === null || onOpen === void 0 || onOpen();
  };
  var handleCloseMenuRequest = function handleCloseMenuRequest() {
    launchButtonElemRef.current = null;
    if (onClose) {
      onClose();
    }
    setOpen(false);
  };
  var renderLaunchButton = function renderLaunchButton() {
    var labelSpan;
    if (label) {
      labelSpan = /*#__PURE__*/React.createElement("span", {
        className: "EmbersMiniSelectLaunchButton-label"
      }, label);
    }
    var numberTag;
    // we show the number tag if a value has been chosen, EXCEPT if the
    //  value chosen is only to show a submenu (unless a chosen value pre-existed the submenu choice)
    if (selectedOptionIndexes.length > 0 && (!isShowingSubmenu || !!valueOnOpen.current)) {
      numberTag = /*#__PURE__*/React.createElement("span", {
        "aria-hidden": true,
        className: "EmbersMiniSelectLaunchButton-tag ".concat(getIconNumberClassName(selectedOptionIndexes.length, true))
      });
    }
    var rightIcon;
    if (loading) {
      rightIcon = /*#__PURE__*/React.createElement(EmbersLoading, {
        className: "EmbersMiniSelectLaunchButton-rightIcon",
        size: 'small'
      });
    } else {
      rightIcon = /*#__PURE__*/React.createElement("span", {
        "aria-hidden": true,
        className: classnames({
          'icon-expand-less': open,
          'icon-expand-more': !open,
          'EmbersMiniSelectLaunchButton-rightIcon': true
        })
      });
    }
    var buttonClassName = classnames({
      'EmbersMiniSelectLaunchButton-root': true,
      'EmbersMiniSelectLaunchButton-menuOpen': open,
      'EmbersMiniSelectLaunchButton-multipleSelect': multiple,
      'EmbersMiniSelectLaunchButton-hasCountIcon': numberTag,
      'EmbersMiniSelectLaunchButton-loading': loading,
      'EmbersMiniSelectLaunchButton-disabled': disabled,
      'EmbersMiniSelectLaunchButton-dark': theme === 'dark',
      'EmbersMiniSelectLaunchButton-hasFocus': hasFocus
    }, className);
    return /*#__PURE__*/React.createElement(MiniSelectLaunchButton, {
      tabIndex: disabled ? -1 : 0,
      ariaProps: {
        'aria-expanded': 'false',
        'aria-haspopup': 'listbox'
      },
      onOpenMenuRequest: handleOpenMenuRequest,
      onCloseMenuRequest: handleCloseMenuRequest,
      onKeyUp: function onKeyUp(e) {
        return onKeyPress(e, ['Tab'], function () {
          return setHasFocus(true);
        });
      },
      onKeyDown: function onKeyDown(e) {
        return onKeyPress(e, ['Tab'], function () {
          return setHasFocus(false);
        });
      },
      menuIsOpen: open,
      className: buttonClassName
    }, labelSpan, numberTag, rightIcon);
  };
  var handleClose = function handleClose(evt) {
    var _launchButtonElemRef$;
    if (isShowingSubmenu || !open) {
      return;
    }
    (_launchButtonElemRef$ = launchButtonElemRef.current) === null || _launchButtonElemRef$ === void 0 || _launchButtonElemRef$.focus();
    if (onClose) {
      onClose();
    }
    launchButtonElemRef.current = null;
    setOpen(false);
  };
  var menu;
  if (open && !loading) {
    if (otherProps.renderMenu) {
      menu = otherProps.renderMenu({
        menuItems: renderMenuItems(),
        anchorEl: launchButtonElemRef.current,
        open: open,
        onClose: handleClose
      });
    } else {
      menu = /*#__PURE__*/React.createElement(EmbersMenu, {
        id: "MiniSelect-menu-".concat(otherProps.name),
        anchorEl: launchButtonElemRef.current,
        open: open,
        onClose: handleClose,
        menuScrollProps: optionsScrollProps
      }, renderMenuItems());
    }
  }
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: miniSelectTheme
  }, renderLaunchButton(), menu);
}